import { useState } from 'react';
import type { ReactElement } from 'react';
import { History } from 'lucide-react';

import { useAudit } from '../api/planning-hooks';
import type { AuditPageResponse } from '../api/planning';
import type { AuditAction, RegistryDocument } from '../api/types';
import { RegistryGate } from '../components/RegistryGate';
import { SummaryChips } from '../components/SummaryChips';
import { PageHeader } from '../components/view-parts';
import { Badge, EmptyState, ErrorState, Pagination, Spinner } from '../components/ui';
import { formatDateTime } from '../lib/date';
import { useQueryParams, toInt } from '../lib/query-params';

const PAGE_SIZE = 50;

const ACTION_LABELS: Record<AuditAction, string> = {
  create: 'создание',
  update: 'изменение',
  delete: 'удаление',
  import: 'импорт',
};

/**
 * F-22: the audit feed — who changed what, when, field by field — with a
 * project filter, an actor filter and pagination (F-20).
 */
export function AuditPage(): ReactElement {
  return <RegistryGate label="Загрузка реестра">{(registry) => <AuditView registry={registry} />}</RegistryGate>;
}

function AuditView({ registry }: { registry: RegistryDocument }): ReactElement {
  const { params, setParams } = useQueryParams();
  const [actor, setActor] = useState(params.actor ?? '');

  const entityId = params.project;
  const page = toInt(params.page) ?? 1;
  const actorQuery = params.actor;

  const query = useAudit({ entityId, actor: actorQuery, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });

  const content = renderFeed({
    data: query.data,
    isPending: query.isPending,
    error: query.error,
    retry: () => void query.refetch(),
    onPage: (page) => setParams({ page: page > 1 ? page : null }),
  });

  return (
    <>
      <PageHeader title="Аудит" sub="Кто, когда и что изменил — по полям, атомарно с самим изменением (F-20)." />

      <SummaryChips />

      <div className="toolbar" role="search" aria-label="Фильтры аудита">
        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="audit-project">
            Проект
          </label>
          <select
            id="audit-project"
            className="input input--compact"
            value={entityId ?? ''}
            onChange={(event) => setParams({ project: event.target.value || undefined, page: null })}
          >
            <option value="">Все записи</option>
            {registry.projects
              .slice()
              .sort((a, b) => a.title.localeCompare(b.title, 'ru'))
              .map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
          </select>
        </div>

        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="audit-actor">
            Пользователь
          </label>
          <input
            id="audit-actor"
            type="search"
            className="input input--compact"
            placeholder=""
            value={actor}
            onChange={(event) => setActor(event.target.value)}
            onBlur={() => setParams({ actor: actor.trim() || undefined, page: null })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setParams({ actor: actor.trim() || undefined, page: null });
            }}
          />
        </div>
      </div>

      <section className="card card--flush" aria-label="Лента аудита">
        <header className="audit-header">
          <History size={16} aria-hidden="true" />
          <span>
            {query.data ? `${query.data.total} записей` : 'Лента изменений'}
          </span>
        </header>
        {content}
      </section>
    </>
  );
}

function renderFeed(args: {
  data: AuditPageResponse | undefined;
  isPending: boolean;
  error: unknown;
  retry: () => void;
  onPage: (page: number) => void;
}): ReactElement {
  const { data, isPending, error, retry, onPage } = args;
  if (!data && isPending) return <Spinner label="Загрузка аудита" />;
  if (!data) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Лента аудита недоступна — вероятно, API ещё не развёрнут (Wave 4).'}
        onRetry={retry}
      />
    );
  }
  if (data.items.length === 0) {
    return (
      <EmptyState
        title="Записей нет"
        hint={data.total > 0 ? 'На этой странице ничего нет — вернитесь к первой.' : 'Изменений по этому фильтру ещё не было.'}
      />
    );
  }

  return (
    <>
      <div className="table-wrap">
        <table className="data">
          <caption className="sr-only">Лента изменений реестра</caption>
          <thead>
            <tr>
              <th scope="col">Когда</th>
              <th scope="col">Кто</th>
              <th scope="col">Действие</th>
              <th scope="col">Объект</th>
              <th scope="col">Изменения</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((entry) => (
              <tr key={entry.id}>
                <td className="cell-muted audit-ts" title={entry.ts}>
                  {formatDateTime(entry.ts)}
                </td>
                <td title={entry.actorEmail}>
                  <span className="cell-primary__text">{entry.actorName}</span>
                  <span className="cell-sub">{entry.actorEmail}</span>
                </td>
                <td>
                  <Badge tone={entry.action === 'delete' ? 'bad' : entry.action === 'create' ? 'ok' : undefined}>
                    {ACTION_LABELS[entry.action]}
                  </Badge>
                </td>
                <td className="cell-primary">
                  <span className="cell-primary__text" title={entry.entityTitle}>
                    {entry.entityTitle}
                  </span>
                  <span className="cell-sub">
                    {entry.entityType} · {entry.entityId}
                  </span>
                </td>
                <td>
                  {entry.changes.length === 0 ? (
                    <span className="cell-muted">—</span>
                  ) : (
                    <ul className="audit-changes">
                      {entry.changes.map((change) => (
                        <li key={`${change.field}-${change.from ?? ''}-${change.to ?? ''}`}>
                          <span className="audit-changes__field">{change.field}</span>{' '}
                          <span className="audit-changes__from">{change.from ?? '∅'}</span> →{' '}
                          <span className="audit-changes__to">{change.to ?? '∅'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination meta={pageMeta(data)} onPage={onPage} />
    </>
  );
}

/**
 * The audit endpoint answers `{items, total, limit, offset}` (arch §3.3); the
 * shared Pagination component speaks `{page, totalPages, …}` — one derivation.
 */
function pageMeta(data: AuditPageResponse): { page: number; totalPages: number; total: number; limit: number } {
  const limit = Math.max(1, data.limit);
  const page = Math.floor(data.offset / limit) + 1;
  return { page, totalPages: Math.max(1, Math.ceil(data.total / limit)), total: data.total, limit };
}
